import React from "react"
import { faArrowRight, faCircleQuestion, faClose, faLightbulb, faListCheck } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../Button/Button"
import ModalPortal from "../Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import { extractYoutubeId } from "../../Objects/Generics/Helps"
import { getHelpVideoLink } from "../../configuration/HelpVideoLinks"

interface PopupHelpButtonProps {
  title: string
  description: string
  bullets?: string[]
  steps?: string[]
  variant?: "global" | "section"
  helpKey?: string
  youtubeLink?: string
}

const urlPattern = /(https?:\/\/[^\s]+)/g

const completeUrlPattern = /^https?:\/\/[^\s]+$/

const renderTextWithLinks = (text: string): React.ReactNode => {
  const parts = text.split(urlPattern)

  return parts.map((part, index) => {
    if (completeUrlPattern.test(part)) {
      return (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-orange-600 underline underline-offset-2 break-all"
          style={{ overflowWrap: "anywhere" }}
          data-interaction-description-key="interactionDescriptions:components.help.popupHelpButton.link.link"
        >
          {part}
        </a>
      )
    }

    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
  })
}

export const PopupHelpButton: React.FC<PopupHelpButtonProps> = ({
  title,
  description,
  bullets = [],
  steps = [],
  variant = "section",
  helpKey,
  youtubeLink,
}) => {
  const [open, setOpen] = React.useState(false)
  const { t } = useTranslation("helpPopup")

  const isGlobal = variant === "global"
  const youtubeId = extractYoutubeId(youtubeLink ?? getHelpVideoLink(helpKey))

  return (
    <>
      <div className={isGlobal ? "rounded-[7px] bg-white/10 backdrop-blur-[1px]" : "rounded-[7px] bg-white"}>
        <ButtonIcon
          typeButton="button"
          sizeNumber={isGlobal ? SIZE.buttonCloseIconSize : SIZE.buttonExpandIcon}
          bgColor={isGlobal ? THEME.colors.buttonBGSaveColor : THEME.colors.buttonBGHelpColor}
          iconColor={THEME.colors.iconBGSecondaryColor}
          icon={faCircleQuestion}
          onClick={() => setOpen(true)}
          size="small"
          descriptionKey="interactionDescriptions:components.help.popupHelpButton.button.setOpenTrue"
        />
      </div>

      {open && (
        <ModalPortal>
          <div className={defaultStyle.overlay}>
            <Animate type={AnimationTypes.fadeInTraslate} duration="0.3s">
              <div className={`${defaultStyle.alert} min-w-0`}>
                <TitlePageResponsiveIconSmall
                  title={title}
                  icon={faLightbulb}
                  iconColor={THEME.colors.iconBGColor}
                  children={
                    <ButtonIcon
                      typeButton="button"
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={() => setOpen(false)}
                      size="small"
                      descriptionKey="interactionDescriptions:components.help.popupHelpButton.button.setOpenFalse"
                    />
                  }
                />

                <Animate type={AnimationTypes.slideUpIn} duration="1.2s">
                  <div className="mt-2 w-full min-w-0 overflow-hidden rounded-lg border-2 border-orange-100 p-3">
                    <div className="flex min-w-0 items-center gap-2 text-orange-700 font-semibold text-sm">
                      <span className="min-w-0 max-w-full break-words text-justify [overflow-wrap:anywhere]">
                        {renderTextWithLinks(description)}
                      </span>
                    </div>
                  </div>
                </Animate>

                {youtubeId && (
                  <Animate type={AnimationTypes.slideUpIn} duration="1.3s">
                    <div className="mt-2 overflow-hidden rounded-lg border-2 border-orange-100 bg-black">
                      <iframe
                        title={`${title}-video`}
                        src={`https://www.youtube.com/embed/${youtubeId}`}
                        className="h-52 w-full md:h-64"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </div>
                  </Animate>
                )}

                {steps.length > 0 && (
                  <Animate type={AnimationTypes.slideUpIn} duration="1.35s">
                    <div className="mt-2 w-full min-w-0 overflow-hidden rounded-lg border-2 border-orange-100 bg-white p-3">
                      <div className="flex min-w-0 items-center gap-2 text-gray-700 font-semibold text-sm mb-3">
                        <FontAwesomeIcon icon={faArrowRight} className="text-orange-500" />

                        <span className="min-w-0 break-words font-bold">{t("step_by_step_label")}</span>
                      </div>

                      <div className="grid min-w-0 gap-2">
                        {steps.map((item, index) => (
                          <Animate type={AnimationTypes.slideUpIn} duration={`${1.45 + index * 0.2}s`} key={index}>
                            <div className="flex w-full min-w-0 flex-col items-center gap-2 overflow-hidden rounded-lg border border-orange-100 bg-orange-50 p-3 text-center text-sm font-semibold text-gray-700 sm:flex-row sm:text-left">
                              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-sm font-bold text-white">
                                {index + 1}
                              </span>

                              <span className="min-w-0 max-w-full flex-1 break-words leading-relaxed [overflow-wrap:anywhere]">
                                {renderTextWithLinks(item)}
                              </span>

                              {index < steps.length - 1 && (
                                <FontAwesomeIcon icon={faArrowRight} className="hidden text-orange-500 sm:block" />
                              )}
                            </div>
                          </Animate>
                        ))}
                      </div>
                    </div>
                  </Animate>
                )}

                {bullets.length > 0 && (
                  <Animate type={AnimationTypes.slideUpIn} duration="1.4s">
                    <div className="mt-1 w-full min-w-0 overflow-hidden rounded-lg border-2 border-orange-100 bg-white p-3">
                      <div className="flex min-w-0 items-center gap-2 text-gray-700 font-semibold text-sm mb-2">
                        <FontAwesomeIcon icon={faListCheck} className="text-orange-500" />

                        <span className="min-w-0 break-words font-bold">{t("quick_tips_label")}</span>
                      </div>

                      <div className="mt-2 min-w-0">
                        <ul className="min-w-0 list-disc space-y-2 pl-5 text-sm text-justify font-medium text-gray-700">
                          {bullets.map((item, index) => (
                            <Animate type={AnimationTypes.slideUpIn} duration={`${1.6 + index * 0.4}s`} key={index}>
                              <li className="mt-4 min-w-0 break-words first:mt-1 leading-relaxed [overflow-wrap:anywhere]">
                                {renderTextWithLinks(item)}
                              </li>
                            </Animate>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Animate>
                )}
              </div>
            </Animate>
          </div>
        </ModalPortal>
      )}
    </>
  )
}

export const PopupHelpIcon = faCircleQuestion
